import React, { useState, useRef, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { setRefreshSidebar } from "../redux/sidebarSlice";
import Swal from "sweetalert2";
import "../css/ChatPage.css";
import send from "../assets/icons/send.png";
import pdfIcon from "../assets/icons/pdf.png";
import summaryIcon from "../assets/icons/summary.png";
import translateIcon from "../assets/icons/translate.png";
import questionIcon from "../assets/icons/question.png";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import { showSuccessToast, showErrorToast } from "../utils/toastUtils";
import "react-toastify/dist/ReactToastify.css";
import { TOAST_MESSAGES } from "../utils/toastMessages";
import { branding } from "../utils/branding";

const PdfToAll = () => {
  const [messages, setMessages] = useState([]);
  const { selectedModel } = useSelector((state) => state.model);
  const [input, setInput] = useState("");
  const [attachedFiles, setattachedFiles] = useState([]);

  const chatEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const [showScrollDown, setShowScrollDown] = useState(false);
  const [isBotResponding, setIsBotResponding] = useState(false);
  const typingIntervalRef = useRef(null);
  const [shouldForceScroll, setShouldForceScroll] = useState(false);
  const [sessionId, setSessionId] = useState(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const user =
    JSON.parse(localStorage.getItem("user")) ||
    JSON.parse(sessionStorage.getItem("user"));

  const loggedInPic = user?.user_profile_pic
    ? `${process.env.REACT_APP_SERVER_URL}/${user.user_profile_pic.replace(
        /\\/g,
        "/"
      )}`
    : "https://cdn-icons-png.flaticon.com/512/3135/3135715.png";

  useEffect(() => {
    const chatBox = document.querySelector(".chat-box");
    if (!chatBox) return;

    const isUserAtBottom =
      chatBox.scrollHeight - chatBox.scrollTop <= chatBox.clientHeight + 50;

    setShowScrollDown(!isUserAtBottom);

    const handleScroll = () => {
      const atBottom =
        chatBox.scrollHeight - chatBox.scrollTop <= chatBox.clientHeight + 50;
      setShowScrollDown(!atBottom);
    };

    chatBox.addEventListener("scroll", handleScroll);

    return () => chatBox.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const chatBox = document.querySelector(".chat-box");

    const handleScroll = () => {
      if (!chatBox) return;

      const isAtBottom =
        chatBox.scrollHeight - chatBox.scrollTop <= chatBox.clientHeight + 50;

      setShowScrollDown(!isAtBottom);
    };

    chatBox?.addEventListener("scroll", handleScroll);
    return () => chatBox?.removeEventListener("scroll", handleScroll);
  }, []);

  const handleStopResponse = () => {
    if (typingIntervalRef.current) {
      clearInterval(typingIntervalRef.current);
    }
    setIsBotResponding(false);
    setMessages((prev) =>
      prev.map((msg) =>
        msg.showDots
          ? { ...msg, showDots: false } // Only stop the dots, preserve partialMessage
          : msg
      )
    );
    window.speechSynthesis.cancel();
  };

  const scrollToBottom = () => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  // Trigger file input click
  const handleAttachClick = () => {
    fileInputRef.current?.click();
  };

  // Handle file selection
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setattachedFiles((prev) => [...prev, ...files]); // append new files
    }
    e.target.value = ""; // reset input to allow re-selecting same file
  };

  // Remove a specific file by index
  const removeAttachedFile = (index) => {
    setattachedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSend = async (customInput) => {
    const finalInput = customInput ?? input;
    if (!finalInput.trim() && !attachedFiles) return;

    const token =
      localStorage.getItem("access_token") ||
      sessionStorage.getItem("access_token");

    if (!token) {
      Swal.fire({
        icon: "error",
        title: "Unauthorized",
        text: "Please login again.",
      });
      return;
    }

    const time = new Date().toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });

    // User message object
    const userMsg = {
      id: Date.now(),
      sender: "user",
      message: finalInput || "Uploaded PDF file",
      time,
      userName: user?.user_name || "User",
      userPic: loggedInPic,
      pdfFile: attachedFiles
        ? {
            name: attachedFiles.name,
            size: `${(attachedFiles.size / 1024 / 1024).toFixed(2)} MB`,
          }
        : null,
    };

    // Add user message to chat
    setMessages((prev) => [...prev, userMsg]);
    setShouldForceScroll(true);
    setInput("");
    setattachedFiles(null);

    // Reset textarea height
    const textarea = document.querySelector(".input-textarea");
    if (textarea) textarea.style.height = "40px";

    // Bot typing placeholder
    const botTypingId = Date.now() + 1;
    const botTypingMsg = {
      id: botTypingId,
      sender: "bot",
      message: "",
      partialMessage: "",
      showDots: true,
      time,
    };
    setMessages((prev) => [...prev, botTypingMsg]);
    setIsBotResponding(true);

    // Fetch bot response from backend
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/chat/save`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            prompt: finalInput,
            model_name: selectedModel,
            session_id: sessionId,
            file: attachedFiles ? attachedFiles.name : null, // optional if backend supports file
          }),
        }
      );

      if (!response.ok) throw new Error("Failed to fetch");

      const botData = await response.json();
      const fullResponse =
        botData.response ||
        "Apologies for the inconvenience — temporary issue processing your request.";

      // Typewriter effect for bot
      let charIndex = 0;
      typingIntervalRef.current = setInterval(() => {
        charIndex++;
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === botTypingId
              ? {
                  ...msg,
                  partialMessage: fullResponse.slice(0, charIndex),
                  showDots: false,
                }
              : msg
          )
        );

        if (charIndex >= fullResponse.length) {
          clearInterval(typingIntervalRef.current);
          typingIntervalRef.current = null;
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === botTypingId
                ? {
                    ...msg,
                    message: fullResponse,
                    partialMessage: "",
                    showDots: false,
                  }
                : msg
            )
          );
          setIsBotResponding(false);
        }
      }, 15);
    } catch (error) {
      clearInterval(typingIntervalRef.current);
      setIsBotResponding(false);
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === botTypingId
            ? {
                ...msg,
                message:
                  "We apologize for the inconvenience. Temporary issue processing your request.",
                partialMessage: "",
                showDots: false,
              }
            : msg
        )
      );
    }
  };
  

  let currentUtterance = null;

  const handleTextToSpeech = (text) => {
    // If already speaking -> stop it first
    if (window.speechSynthesis.speaking) {
      window.speechSynthesis.cancel();
      // If same message clicked again → stop sound
      if (currentUtterance && currentUtterance.text === text) {
        currentUtterance = null;
        return;
      }
    }

    // Create new utterance
    const utterance = new SpeechSynthesisUtterance(text);
    currentUtterance = utterance;

    // Optional: Set voice, pitch, rate, etc.
    utterance.lang = "en-US";
    utterance.rate = 1;
    utterance.pitch = 1;

    // When finished → reset current utterance
    utterance.onend = () => {
      currentUtterance = null;
    };

    // Speak
    window.speechSynthesis.speak(utterance);
  };

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      showSuccessToast("Text copied to clipboard!");
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const handleRetry = (botMsgId) => {
    const botIndex = messages.findIndex((msg) => msg.id === botMsgId);
    for (let i = botIndex - 1; i >= 0; i--) {
      if (messages[i].sender === "user") {
        handleSend(messages[i].message);
        break;
      }
    }
  };

  const renderMessage = (msg) => {
    const isBot = msg.sender === "bot";
    const profile = isBot
      ? "/img/botIcon.png"
      : msg.userPic ||
        loggedInPic ||
        "https://cdn-icons-png.flaticon.com/512/3135/3135715.png";

    const userName = isBot
      ? branding("ZeroBox")
      : msg.userName || user?.user_name || "User";

    return (
      <div
        className={`d-flex ${
          isBot ? "flex-row" : "flex-row-reverse"
        } align-items-start my-3`}
        key={msg.id}
      >
        <div className="d-flex flex-column chat-message-container">
          {/* Chat Bubble */}
          <div
            className={`chat-bubble ${isBot ? "bot-bubble" : "user-bubble"}`}
          >
            <div className="d-flex align-items-center gap-2 mb-2">
              <img
                src={profile}
                className={isBot ? "bot-avatar" : "chat-avatar"}
                alt="avatar"
              />
              <strong>{isBot ? branding("ZeroBox") : userName}</strong>
              <small className="">{msg.time}</small>
            </div>

            <div className="formatted-text">
              {isBot && msg.showDots ? (
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              ) : (
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeRaw]}
                  components={{
                    code({ node, inline, className, children, ...props }) {
                      const match = /language-(\w+)/.exec(className || "");
                      return !inline && match ? (
                        <SyntaxHighlighter
                          style={oneDark}
                          language={match[1]}
                          PreTag="div"
                          customStyle={{
                            borderRadius: "10px",
                            padding: "1rem",
                          }}
                          {...props}
                        >
                          {String(children).replace(/\n$/, "")}
                        </SyntaxHighlighter>
                      ) : (
                        <code
                          className="bg-light px-2 py-1 rounded"
                          style={{ fontFamily: "monospace" }}
                          {...props}
                        >
                          {children}
                        </code>
                      );
                    },
                  }}
                >
                  {msg.partialMessage || msg.message}
                </ReactMarkdown>
              )}
            </div>

            {/* PDF Attachment (if any) */}
            {msg.pdfFile && (
              <div className="mt-3 p-2 bg-light w-100 rounded">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <div className="d-flex align-items-center gap-3">
                    <img src={pdfIcon} alt="pdf icon" width="24" />
                    <div>
                      <div className="fw-semibold">{msg.pdfFile.name}</div>
                      <div className="text-muted">{msg.pdfFile.size}</div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Action icons (Copy, Sound, Retry, etc.) */}
          {!msg.showDots && (
            <div
              className={`d-flex bot-below-icon gap-3 mt-1 ${
                isBot
                  ? "justify-content-start ms-3"
                  : "justify-content-end me-3"
              }`}
            >
              {isBot ? (
                <>
                  <i
                    className={`bi refresh-prompts ${
                      window.speechSynthesis.speaking &&
                      currentUtterance?.text === msg.message
                        ? "bi-volume-mute"
                        : "bi-volume-up"
                    }`}
                    role="button"
                    title={
                      window.speechSynthesis.speaking &&
                      currentUtterance?.text === msg.message
                        ? "Stop Sound"
                        : "Play Sound"
                    }
                    onClick={() => handleTextToSpeech(msg.message)}
                  />

                  <i
                    className="bi bi-clipboard"
                    role="button"
                    title="Copy"
                    onClick={() => handleCopy(msg.message)}
                  />
                  <i
                    className="bi bi-arrow-clockwise"
                    role="button"
                    title="Retry"
                    onClick={() => handleRetry(msg.id)}
                  />
                </>
              ) : (
                <>
                  <i
                    className="bi bi-clipboard"
                    role="button"
                    title="Copy"
                    onClick={() => handleCopy(msg.message)}
                  />
                </>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="w-100 h-100 overflow-hidden chat-wrapper">
      {/* Chat Area */}
      <div className="flex-grow-1 mt-5 overflow-auto chat-box">
        {messages.map((msg) => renderMessage(msg))}
        <div ref={chatEndRef} />
        {showScrollDown && (
          <button
            className="scroll-down-btn"
            onClick={() =>
              chatEndRef.current?.scrollIntoView({ behavior: "smooth" })
            }
          >
            <i className="bi bi-arrow-down" style={{ fontSize: "24px" }}></i>
          </button>
        )}
      </div>

      {/* Input Area */}
      <div className="mx-1">
        <div
          className="rounded p-1 mt-1 input-area"
          style={{ border: "1px solid var(--dark-color)" }}
        >
          {/* PDF Previews as flex-wrap chips */}
          {attachedFiles.length > 0 && (
            <div className="d-flex flex-wrap mb-2">
              {attachedFiles.map((file, idx) => (
                <div
                  key={idx}
                  className="attached-file-chip position-relative mb-2 me-2 rounded bg-light d-flex flex-column px-2 py-1"
                  style={{
                    maxWidth: "200px",
                    overflow: "visible",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                  }}
                >
                  {/* Top row: icon + name */}
                  <div
                    className="d-flex align-items-center gap-1"
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: "100%",
                    }}
                  >
                    <img src={pdfIcon} alt="PDF" width="18" />
                    <span style={{fontSize:"small"}} title={file.name}>{file.name}</span>
                  </div>

                  {/* File size below */}
                  <small style={{ fontSize: "10px", color: "#555" }}>
                    {(file.size / (1024 * 1024)).toFixed(2)} MB
                  </small>

                  {/* Top-right remove button */}
                  <button
                    type="button"
                    onClick={() => removeAttachedFile(idx)}
                    style={{
                      position: "absolute",
                      top: "-6px",
                      right: "-6px",
                      background: "#ff4d4f",
                      border: "none",
                      color: "white",
                      borderRadius: "50%",
                      width: "18px",
                      height: "18px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      fontSize: "12px",
                      padding: 0,
                    }}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}

          <textarea
            className="p-2 pb-4 border-0 shadow-none input-textarea"
            placeholder={branding("How can ZeroBox help you today?")}
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              e.target.style.height = "auto";
              e.target.style.height = e.target.scrollHeight + "px";
            }}
            rows={1}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                if (!isBotResponding) handleSend();
                e.target.style.height = "40px";
              }
            }}
          />

          <div className="d-flex align-items-center justify-content-between mt-1">
            <div className="d-flex gap-1">
              <button className="btn btn-link p-0" onClick={handleAttachClick}>
                <i className="bi bi-paperclip fs-5"></i>
              </button>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept=".pdf"
                style={{ display: "none" }}
                multiple
              />
            </div>
            <button
              className="custom-send-btn d-flex align-items-center justify-content-center"
              onClick={isBotResponding ? handleStopResponse : handleSend}
            >
              {isBotResponding ? (
                <i
                  className="bi bi-stop-fill rounded-circle d-flex align-items-center justify-content-center text-white"
                  style={{
                    width: "30px",
                    height: "27px",
                    background: "#3e3e3e",
                    border: "none",
                  }}
                ></i>
              ) : (
                <img src={send} alt="Send" title="Send" />
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PdfToAll;
